/**
 * Typer for ExternalSystem-administrasjon (vareheis-/eksternsystem-konfig).
 * Speiler ExternalSystemAdminDTOs.cs. ApiToken eksponeres aldri fra API-et —
 * kun hasApiToken; token settes/erstattes via create/update-requestene.
 */

export interface ExternalSystemAdmin {
  id: number;
  companyNo: string;
  externalSystemID: number;
  externalCustomID?: string | null;
  description1?: string | null;
  operatorID?: string | null;
  profileID?: string | null;
  /** 45 = Til pakking, 50 = Klar for eksport (direkteavslutning) */
  importPListStatus?: number | null;
  activeYesNo: boolean;
  /** Bitmaske: 1=plukk eksport, 2=plukk ferdigmelding (4/8/32 = senere faser) */
  docTypeActive: number;
  providerKey?: string | null;
  apiBaseUrl?: string | null;
  hasApiToken: boolean;
  confirmationNextId: number;
  updatedBy?: string | null;
  lastUpdate?: string | null;
  createdBy?: string | null;
  created?: string | null;
}

export interface CreateExternalSystemAdminRequest {
  externalSystemID: number;
  externalCustomID?: string;
  description1?: string;
  operatorID?: string;
  profileID?: string;
  importPListStatus?: number;
  activeYesNo: boolean;
  docTypeActive: number;
  providerKey?: string;
  apiBaseUrl?: string;
  apiToken?: string;
}

export interface UpdateExternalSystemAdminRequest {
  externalSystemID?: number;
  externalCustomID?: string;
  description1?: string;
  operatorID?: string;
  profileID?: string;
  importPListStatus?: number;
  activeYesNo?: boolean;
  docTypeActive?: number;
  providerKey?: string;
  apiBaseUrl?: string;
  /** undefined = behold eksisterende token, tom streng = fjern, verdi = erstatt */
  apiToken?: string;
  confirmationNextId?: number;
}

/** Kjente providere (må matche VerticalLiftProviderKeys i backend) */
export const EXTERNAL_SYSTEM_PROVIDERS = ['Shark', 'MockLift'] as const;

/** DocTypeActive-bits som er implementert (må matche ExternalSystemDocTypes i backend) */
export const DOC_TYPE_BITS = {
  pickExport: 1,
  pickConfirmation: 2,
  putAwayExport: 4,
  putAwayConfirmation: 8,
  stockCountConfirmation: 32
} as const;

/** Statuskort for en heis (GET externalsystems/{id}/status). Speiler VerticalLiftStatusDto. */
export interface VerticalLiftStatus {
  id: number;
  externalSystemID: number;
  lastContactAt?: string | null;
  lastErrorAt?: string | null;
  lastError?: string | null;
  confirmationNextId: number;
  pickQueued: number;
  pickSent: number;
  oldestPickSentAt?: string | null;
  putAwayQueued: number;
  putAwaySent: number;
  oldestPutAwaySentAt?: string | null;
  countsAwaitingApproval: number;
  lastEventAt?: string | null;
  /** Flyter i workeren som feiler nå (Pick = sending av plukk, PutAway = sending av utlegg, Confirmations = henting av bekreftelser) */
  openFailures: VerticalLiftOpenFailure[];
  /** Køtallene kunne ikke leses (ordrelinjene låst) — de øvrige feltene er gyldige */
  queueUnavailable: boolean;
}

export interface VerticalLiftOpenFailure {
  flow: string;
  since: string;
  message?: string | null;
}

/** Én hendelse i aktivitetsloggen (GET externalsystems/{id}/activity). Speiler VerticalLiftEventDto. */
export interface VerticalLiftEvent {
  id: number;
  eventTime: string;
  /** Sent | SendFailed | Confirmed | Finalized | CountRegistered | Skipped | ApplyFailed | ContactFailed | ContactRestored */
  eventType: string;
  /** Pick | PutAway | StockCount | Other — null for hendelser som gjelder heisen (kontakt) */
  flow?: string | null;
  orderNumber?: string | null;
  documentID?: number | null;
  quantity?: number | null;
  isError: boolean;
  message?: string | null;
  createdBy?: string | null;
  /** Plukklistens/utleggsordrens beskrivelse da hendelsen skjedde (ofte kunde + ERP-ordrenumre) */
  description?: string | null;
}

export interface VerticalLiftActivityFilter {
  from?: string;
  flow?: string;
  eventType?: string;
  onlyErrors?: boolean;
  take?: number;
}

export const LIFT_EVENT_TYPES = [
  'Sent', 'SendFailed', 'Confirmed', 'Finalized', 'CountRegistered',
  'Skipped', 'ApplyFailed', 'ContactFailed', 'ContactRestored', 'ProcessingFailed', 'ProcessingRestored'
] as const;

export const LIFT_FLOWS = ['Pick', 'PutAway', 'StockCount'] as const;

/** En ordre i kø til eller sendt til heisen (GET externalsystems/{id}/pending). Speiler VerticalLiftPendingOrderDto. */
export interface VerticalLiftPendingOrder {
  /** Pick | PutAway */
  flow: string;
  /** Queued | Sent */
  state: string;
  orderNumber: string;
  description1?: string | null;
  /** Sendt-tidspunkt for sendte ordrer, opprettet-tidspunkt for ordrer i kø */
  since?: string | null;
  lines: number;
  quantity?: number | null;
}
